import { z } from 'zod'

export const CRM_MESSAGE_CONTRACT_VERSION = '1.0.0' as const

const nullableString = z.string().nullable()
const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by every CRM message read alias. */
export const crmMessageReadQuerySchema = z.object({
  orgId: optionalQueryString,
  org_id: optionalQueryString,
  limit: optionalQueryString,
  cursor: optionalQueryString,
  matchStatus: optionalQueryString,
  companyId: optionalQueryString,
}).passthrough()

export const crmMessageReadSideEffectsSchema = z.object({
  db1Written: z.literal(false),
  auditWritten: z.literal(false),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmMessageChannelSchema = z.enum(['email', 'whatsapp', 'other'])
export const crmMessageDirectionSchema = z.enum(['inbound', 'outbound'])
export const crmMessageMatchStatusSchema = z.enum(['matched', 'unmatched', 'ambiguous'])
export const crmMessageExtractionStatusSchema = z.enum([
  'pending',
  'extracted',
  'failed',
  'skipped',
  'parsed',
  'llm_submitted',
  'analyzed',
  'assigned',
])

export const crmMessageSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  channel: crmMessageChannelSchema,
  direction: crmMessageDirectionSchema,
  externalMessageId: z.string().min(1),
  threadKey: nullableString,
  fromIdentifier: nullableString,
  toIdentifiers: z.array(z.string()),
  subject: nullableString,
  bodyRef: nullableString,
  bodyPresent: z.boolean(),
  receivedAt: z.string().min(1),
  companyId: nullableString,
  contactId: nullableString,
  matchStatus: crmMessageMatchStatusSchema,
  extractionStatus: crmMessageExtractionStatusSchema,
  extractionAttemptedAt: nullableString,
  emailType: nullableString,
  agentSubmissionId: nullableString,
  interactionId: nullableString,
  sourceId: z.string().min(1),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const crmMessageProposedChangesSchema = z.object({
  company: z.object({
    segment: z.string().optional(),
    tier: z.string().optional(),
    notes: z.string().optional(),
  }).optional(),
  contact: z.object({
    title: z.string().optional(),
  }).optional(),
  tasks: z.array(z.object({
    title: z.string(),
    description: z.string().optional(),
    dueAt: nullableString.optional(),
  }).passthrough()).optional(),
}).passthrough()

export const crmMessageProposalSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  subjectType: z.enum(['crm_message', 'crm_transcript', 'crm_company', 'crm_contact']),
  subjectId: z.string().min(1),
  source: z.enum(['llm_extraction', 'provider_enrichment', 'manual']),
  model: nullableString,
  proposedChanges: crmMessageProposedChangesSchema,
  status: z.enum(['proposed', 'approved', 'rejected', 'applied']),
  createdByUserId: nullableString,
  reviewedByUserId: nullableString,
  reviewedAt: nullableString,
  appliedAt: nullableString,
  appliedResult: z.record(z.unknown()).nullable(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const crmMessageListResponseSchema = z.object({
  contract_version: z.literal(CRM_MESSAGE_CONTRACT_VERSION),
  messages: z.array(crmMessageSummarySchema),
  nextCursor: nullableString,
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const crmMessageDetailResponseSchema = z.object({
  contract_version: z.literal(CRM_MESSAGE_CONTRACT_VERSION),
  message: crmMessageSummarySchema,
  proposals: z.array(crmMessageProposalSchema),
  sideEffects: crmMessageReadSideEffectsSchema,
}).passthrough()

export const CRM_MESSAGE_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmMessageErrorEnvelopeSchema = z.object({
  contract_version: z.literal(CRM_MESSAGE_CONTRACT_VERSION),
  error: z.object({
    code: z.enum(CRM_MESSAGE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
    details: z.record(z.unknown()).optional(),
    request_id: z.string().optional(),
  }),
}).passthrough()

export type CrmMessageReadQuery = z.infer<typeof crmMessageReadQuerySchema>
export type CrmMessageReadSideEffects = z.infer<typeof crmMessageReadSideEffectsSchema>
export type CrmMessageChannel = z.infer<typeof crmMessageChannelSchema>
export type CrmMessageDirection = z.infer<typeof crmMessageDirectionSchema>
export type CrmMessageMatchStatus = z.infer<typeof crmMessageMatchStatusSchema>
export type CrmMessageExtractionStatus = z.infer<typeof crmMessageExtractionStatusSchema>
export type CrmMessageSummary = z.infer<typeof crmMessageSummarySchema>
export type CrmMessageProposedChanges = z.infer<typeof crmMessageProposedChangesSchema>
export type CrmMessageProposal = z.infer<typeof crmMessageProposalSchema>
export type CrmMessageListResponse = z.infer<typeof crmMessageListResponseSchema>
export type CrmMessageDetailResponse = z.infer<typeof crmMessageDetailResponseSchema>
export type CrmMessageErrorCode = (typeof CRM_MESSAGE_ERROR_CODES)[number]
export type CrmMessageErrorEnvelope = z.infer<typeof crmMessageErrorEnvelopeSchema>
